import { Component, Input } from '@angular/core';
import {
	Document,
	NewDocumentRequest,
	JobsClient,
	DocumentsClient,
	CustomerClient,
} from 'src/shared/api';
import { FormGroup, FormBuilder, NgControl, FormArray } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { animateList } from 'src/shared/animations';
import { AddDocumentDialogComponent } from '../add-document-dialog/add-document-dialog.component';
import { DocumentTarget } from '../documents.models';
import { CustomFormListAccessor } from 'src/shared/form-models/form-list-cva.component';
import { map, concatMap, filter } from 'rxjs/operators';
import { Observable } from 'rxjs';
import { downloadDocument, previewDocument } from '../documents.utility';
import { documentFormConfig } from '../edit-document/edit-document.component';
import { EditDocumentDialogComponent } from '../edit-document-dialog/edit-document-dialog.component';
import { AddDocumentDragAndDropDialogComponent } from '../add-document-dnd-dialog/add-document-dnd-dialog.component';

@Component({
	selector: 'rpshared-document-list',
	templateUrl: './document-list.component.html',
	styleUrls: ['./document-list.scss'],
	animations: [animateList],
})
export class DocumentListComponent extends CustomFormListAccessor<Document> {
	@Input() parentId: number;
	@Input() target: keyof typeof DocumentTarget;

	readonly emptyColumns = ['empty'];
	readonly resultColumns = [
		'title',
		'uploadedOn',
		'hiddenFromCustomer',
		'hiddenFromFieldWorker',
		'includedInInvoicing',
		'download',
		'preview',
		'edit',
		'delete',
	];

	readonly uploadMap: {
		[K in keyof typeof DocumentTarget]: (
			request: NewDocumentRequest
		) => Observable<Document>;
	} = {
		Customer: (request: NewDocumentRequest) =>
			this.customerClient.addDocumentToCustomer(request),
		Railroad: (request: NewDocumentRequest) =>
			this.customerClient.addDocumentToRailroad(request),
		Job: (request: NewDocumentRequest) =>
			this.jobsClient.addDocumentToJob(request),
	};

	constructor(
		public ngControl: NgControl,
		public fb: FormBuilder,
		private readonly dialog: MatDialog,
		private readonly jobsClient: JobsClient,
		private readonly customerClient: CustomerClient,
		private readonly documentsClient: DocumentsClient
	) {
		super(ngControl, fb, documentFormConfig);
	}

	addDocument() {
		if (this.target === DocumentTarget.Job) {
			this.dialog
				.open<
					AddDocumentDragAndDropDialogComponent,
					{
						parentId: number;
					},
					FormGroup
				>(AddDocumentDragAndDropDialogComponent, {
					width: '900px',
					disableClose: true,
					data: {
						parentId: this.parentId,
					},
				})
				.afterClosed()
				.pipe(
					filter<FormGroup>(Boolean),
					map((formGroup) => formGroup),
					map((document) =>
						document.value.documents.map((doc: Document) => {
							return {
								id: doc.id,
								title: doc.title,
								fileName: doc.fileName,
								description: doc.description,
								uploadedOn: doc.uploadedOn,
								timeStamp: doc.timeStamp,
								tags: [''],
								sizeInBytes: 0,
								hideFromCustomer: doc.hideFromCustomer,
								hideFromFieldWorker: doc.hideFromFieldWorker,
								includeInInvoicing: doc.includeInInvoicing,
							};
						})
					)
				)
				.subscribe((documents: Document[]) => {
					for (const document of documents) {
						if (document.id !== 0) {
							const form = this.fb.group(documentFormConfig) as FormGroup;
							form.setValue(document);
							this.array.insert(0, form);
						}
					}
				});
		} else {
			this.dialog
				.open<AddDocumentDialogComponent, never, FormGroup>(
					AddDocumentDialogComponent,
					{ width: '450px' }
				)
				.afterClosed()
				.pipe(
					filter<FormGroup>(Boolean),
					map((formGroup) => formGroup.value),
					map((request: NewDocumentRequest) => ({
						...request,
						parentId: this.parentId,
					})),
					concatMap((request) =>
						this.uploadMap[this.target](request)
					),
					map((document) => {
						const form = this.fb.group(documentFormConfig) as FormGroup;
						form.setValue(document);
						return form;
					})
				)
				.subscribe((formGroup) => this.array.insert(0, formGroup));
		}
	}

	deleteDocument(docId: number) {
		const index = this.array.value.findIndex(
			(document: Document) => document.id === docId
		);
		this.documentsClient
			.deleteForDocument(docId)
			.subscribe(() => this.array.removeAt(index));
	}

	editDocument(doc: Document) {
		this.dialog
			.open<EditDocumentDialogComponent, Document, FormGroup>(
				EditDocumentDialogComponent,
				{
					width: '450px',
					data: doc,
				}
			)
			.afterClosed()
			.pipe(filter((control) => !!control))
			.subscribe((control) => {
				const arr = this.parent.get('array') as FormArray;
				const index = arr.value.findIndex(
					(d: Document) => d.id === doc.id
				);
				if (index !== -1) {
					arr.setControl(index, control);
				}
			});
	}

	downloadDocument(id: number) {
		this.documentsClient
			.getBinaryDataForDocument(id)
			.subscribe(downloadDocument);
	}

	previewDocument(id: number) {
		this.documentsClient
			.getBinaryDataForDocument(id)
			.subscribe(previewDocument);
	}
}
